import { useState } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { Trash2Icon } from "lucide-react";

import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { DELETE_ACCOUNT_CONFIRMATION, DeleteAccountSchema, deleteAccountSchema } from "@/features/account/schemas";
import { deleteAccount } from "@/features/account/services/account";
import { AccountStats } from "@/features/account/types";
import Error from "@/shared/components/Error";
import { useLogout } from "@/shared/hooks/useLogout";
import { AppError } from "@/shared/lib/utils/errors";

interface DeleteAccountFormProps {
  stats?: AccountStats;
}

export default function DeleteAccountForm({ stats }: DeleteAccountFormProps) {
  const [open, setOpen] = useState(false);

  const { logout } = useLogout();

  const form = useForm<DeleteAccountSchema>({
    resolver: zodResolver(deleteAccountSchema),
    defaultValues: {
      confirmation: "",
    },
  });

  const mutation = useMutation<undefined, AppError | Error, DeleteAccountSchema>({
    mutationFn: deleteAccount,
    onSuccess: () => {
      // Clears the cached session and query cache, then returns to the login page.
      logout();
    },
  });

  async function onSubmit(values: DeleteAccountSchema) {
    mutation.mutate(values);
  }

  const isBusy = mutation.isPending || mutation.isSuccess;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!isBusy) {
          form.reset();
          mutation.reset();
          setOpen(newOpen);
        }
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="sm" className="w-fit">
          <Trash2Icon />
          Delete account
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete your account?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <p>
                  This permanently deletes your account
                  {stats && (
                    <>
                      , removes you from{" "}
                      <strong>
                        {stats.projectCount} {stats.projectCount === 1 ? "project" : "projects"}
                      </strong>{" "}
                      and unassigns you from every ticket
                    </>
                  )}
                  . Tickets and comments you authored remain, but are no longer attributed to you. This action <strong>cannot be undone</strong>.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            {mutation.isError && <Error title="Unable to delete account" error={mutation.error} />}
            <Controller
              name="confirmation"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>
                    Type <span className="font-mono font-semibold text-foreground">{DELETE_ACCOUNT_CONFIRMATION}</span> to confirm
                  </FieldLabel>
                  <Input {...field} id={field.name} disabled={isBusy} aria-invalid={fieldState.invalid} placeholder={DELETE_ACCOUNT_CONFIRMATION} autoComplete="off" spellCheck={false} />
                  <FieldDescription>This safeguard prevents accidental deletion.</FieldDescription>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isBusy}>Cancel</AlertDialogCancel>
              <Button type="submit" variant="destructive" className="disabled:opacity-40" disabled={isBusy}>
                <Trash2Icon />
                {isBusy ? "Deleting account..." : "Delete account"}
              </Button>
            </AlertDialogFooter>
          </form>
        </FormProvider>
      </AlertDialogContent>
    </AlertDialog>
  );
}
